import { useEffect, useMemo, useState } from 'react';
import { Alert, Input, Select, Space, Spin, Table, Tag, Typography } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import SDIModal from '../../../../components/ui/SDIModal';
import axiosInstance from '../../../../variables/api/axiosInstance';
import { frenchDateOnly } from '../../../../variables/functions';

const { Text } = Typography;

// Valeur de l'option « retirer l'intermédiaire » (le backend reçoit alors un code vide).
const AUCUN = '__AUCUN__';

const montant = (v) => `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(v || 0)} F`;
const taux = (v) => (v == null ? '—' : `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(v)} %`);

const nomIntermediaire = (i) => {
    if (!i) return null;
    const nom = `${i.personne?.nom || ''} ${i.personne?.prenom || ''}`.trim();
    return nom ? `${nom} (${i.codeIntermediaire})` : i.codeIntermediaire;
};

const libelleIntermediaire = (nom, code) => {
    if (!code) return 'Aucun';
    return nom ? `${nom} (${code})` : code;
};

/**
 * Changement d'intermédiaire sur un contrat DÉJÀ ENCAISSÉ, hors avenant (habilitation HCO107).
 * L'aperçu serveur liste les mouvements de la police avec la commission avant / après ; les
 * mouvements dont la commission est déjà sur un bordereau ne sont pas sélectionnables.
 */
const ChangementIntermediaire = ({ open, update, contrat, onRefresh }) => {
    const [intermediaires, setIntermediaires] = useState([]);
    const [loadingListe, setLoadingListe] = useState(false);
    const [choix, setChoix] = useState(null);
    const [apercu, setApercu] = useState(null);
    const [loadingApercu, setLoadingApercu] = useState(false);
    const [selection, setSelection] = useState([]);
    const [motif, setMotif] = useState('');
    const [saving, setSaving] = useState(false);

    const codeOrganisme = contrat?.organisme?.code;
    const codeActuel = contrat?.intermediaire?.codeIntermediaire || null;

    const messageErreur = (err, defaut) => {
        if (err?.response?.status === 403) {
            return err?.response?.data?.message || "Vous n'avez pas le droit de changer l'intermédiaire de ce contrat.";
        }
        return err?.response?.data?.message || defaut;
    };

    useEffect(() => {
        if (!open) return;
        setChoix(null);
        setApercu(null);
        setSelection([]);
        setMotif('');
        if (!codeOrganisme) return;
        setLoadingListe(true);
        axiosInstance
            .get(`/api/intermediaire/compagnie/actifs/${codeOrganisme}`)
            .then((res) => setIntermediaires(Array.isArray(res.data) ? res.data : []))
            .catch(() => toast.error('Erreur lors du chargement des intermédiaires'))
            .finally(() => setLoadingListe(false));
    }, [open, codeOrganisme]);

    const chargerApercu = (valeur) => {
        setChoix(valeur);
        setApercu(null);
        setSelection([]);
        if (!valeur) return;
        setLoadingApercu(true);
        axiosInstance
            .get(`/api/contrat/${contrat?.codeId}/changement-intermediaire/apercu`, {
                params: valeur === AUCUN ? {} : { intermediaireCode: valeur },
            })
            .then((res) => {
                setApercu(res.data);
                // Coché par défaut : mouvements modifiables qui portent le même intermédiaire que le
                // mouvement affiché (la saisie erronée s'est propagée aux avenants suivants).
                setSelection((res.data?.mouvements || [])
                    .filter((m) => m.modifiable && m.memeIntermediaireQueVersionAffichee)
                    .map((m) => m.codeId));
            })
            .catch((err) => toast.error(messageErreur(err, "Erreur lors du calcul de l'aperçu")))
            .finally(() => setLoadingApercu(false));
    };

    const options = useMemo(() => {
        const liste = intermediaires
            .map((i) => ({
                value: i.codeIntermediaire,
                label: `${nomIntermediaire(i)}${i.codeIntermediaire === codeActuel ? ' — actuel' : ''}`,
                disabled: i.codeIntermediaire === codeActuel,
            }))
            .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
        if (codeActuel) {
            liste.unshift({ value: AUCUN, label: "Aucun intermédiaire (retirer l'intermédiaire)" });
        }
        return liste;
    }, [intermediaires, codeActuel]);

    const mouvementsChoisis = (apercu?.mouvements || []).filter((m) => selection.includes(m.codeId));
    const totalAvant = mouvementsChoisis.reduce((s, m) => s + (m.commissionActuelle || 0), 0);
    const totalApres = mouvementsChoisis.reduce((s, m) => s + (m.commissionNouvelle || 0), 0);

    const handleSubmit = () => {
        if (!choix) {
            toast.error('Veuillez choisir le nouvel intermédiaire');
            return;
        }
        if (selection.length === 0) {
            toast.error('Sélectionnez au moins un mouvement');
            return;
        }
        if (!motif.trim()) {
            toast.error('Le motif est obligatoire');
            return;
        }
        setSaving(true);
        axiosInstance
            .put(`/api/contrat/${contrat?.codeId}/changement-intermediaire`, {
                intermediaireCode: choix === AUCUN ? null : choix,
                mouvements: selection,
                motif: motif.trim(),
            })
            .then((res) => {
                const n = res.data?.mouvements?.length || selection.length;
                toast.success(`Intermédiaire modifié sur ${n} mouvement(s)`);
                update(false);
                if (onRefresh) onRefresh();
                else window.location.reload();
            })
            .catch((err) => toast.error(messageErreur(err, "Erreur lors du changement d'intermédiaire")))
            .finally(() => setSaving(false));
    };

    const colonnes = [
        {
            title: 'Mouvement',
            key: 'mouvement',
            render: (_, m) => (
                <Space direction="vertical" size={0}>
                    <Space size={4}>
                        <Text strong>{m.avenant || '—'}</Text>
                        {m.versionAffichee && <Tag color="blue">affiché</Tag>}
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {frenchDateOnly(m.dateEffet)} → {frenchDateOnly(m.dateEcheance)}
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Intermédiaire actuel',
            key: 'intermediaire',
            render: (_, m) => libelleIntermediaire(m.intermediaireNom, m.intermediaireCode),
        },
        {
            title: 'Règlements',
            key: 'reglements',
            align: 'right',
            render: (_, m) => (
                <Space direction="vertical" size={0} style={{ textAlign: 'right' }}>
                    <span>{m.nbReglements}</span>
                    {m.nbReglements > 0 && (
                        <Text type="secondary" style={{ fontSize: 12 }}>{montant(m.montantRegle)}</Text>
                    )}
                </Space>
            ),
        },
        {
            title: 'Taux',
            key: 'taux',
            align: 'right',
            render: (_, m) => `${taux(m.tauxActuel)} → ${taux(m.tauxNouveau)}`,
        },
        {
            title: 'Commission',
            key: 'commission',
            align: 'right',
            render: (_, m) => `${montant(m.commissionActuelle)} → ${montant(m.commissionNouvelle)}`,
        },
        {
            title: 'État',
            key: 'etat',
            render: (_, m) => (m.modifiable
                ? <Tag color="green">Modifiable</Tag>
                : <Text type="danger" style={{ fontSize: 12 }}>{m.motifBlocage}</Text>),
        },
    ];

    return (
        <SDIModal
            title="CHANGEMENT D'INTERMÉDIAIRE"
            confirmLoading={saving}
            centered
            open={open}
            onOk={handleSubmit}
            onCancel={() => update(false)}
            okText="Confirmer le changement"
            okButtonProps={{ disabled: !choix || selection.length === 0 || !motif.trim() }}
            cancelText="Annuler"
            width={1000}
            zIndex={1500}
        >
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Correction sans avenant d'un contrat déjà encaissé"
                description={"La police et les montants encaissés ne changent pas. La commission des règlements "
                    + "bascule vers le nouvel intermédiaire, à son taux (même assiette). Impossible pour un "
                    + "mouvement dont la commission est déjà sur un bordereau. Opération journalisée."}
            />

            <div style={{ marginBottom: 16 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Intermédiaire actuel</Text>
                <br />
                <Text strong style={{ fontSize: 15 }}>
                    <TeamOutlined style={{ marginRight: 6 }} />
                    {nomIntermediaire(contrat?.intermediaire) || 'Aucun'}
                </Text>
            </div>

            <Text strong>Nouvel intermédiaire</Text>
            <Select
                showSearch
                allowClear
                style={{ width: '100%', marginTop: 8, marginBottom: 16 }}
                placeholder="Rechercher un intermédiaire de la compagnie..."
                value={choix || undefined}
                onChange={chargerApercu}
                loading={loadingListe}
                optionFilterProp="label"
                options={options}
                size="large"
            />

            {loadingApercu && <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>}

            {apercu && (
                <>
                    {(apercu.avertissements || []).map((a, idx) => (
                        <Alert key={idx} type="warning" showIcon style={{ marginBottom: 12 }} message={a} />
                    ))}
                    <Table
                        rowKey="codeId"
                        size="small"
                        pagination={false}
                        columns={colonnes}
                        dataSource={apercu.mouvements || []}
                        rowSelection={{
                            selectedRowKeys: selection,
                            onChange: (keys) => setSelection(keys),
                            getCheckboxProps: (m) => ({ disabled: !m.modifiable }),
                        }}
                        scroll={{ x: true }}
                    />
                    <div style={{ textAlign: 'right', margin: '12px 0 16px' }}>
                        <Text>
                            {selection.length} mouvement(s) sélectionné(s) — commission{' '}
                            <Text strong>{montant(totalAvant)}</Text> → <Text strong>{montant(totalApres)}</Text>
                        </Text>
                    </div>
                    <Text strong>Motif</Text>
                    <Input.TextArea
                        rows={3}
                        style={{ marginTop: 8 }}
                        maxLength={200}
                        showCount
                        placeholder="Ex. : intermédiaire saisi par erreur à la souscription"
                        value={motif}
                        onChange={(e) => setMotif(e.target.value)}
                    />
                </>
            )}
        </SDIModal>
    );
};

export default ChangementIntermediaire;
